/**
 * Strip Beehiiv web embed styles that force a light canvas / dark text so
 * `.nl-post-content` dark-theme CSS can apply reliably.
 */

function parseRgb(rgb: string): [number, number, number] | null {
  const m = rgb.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function parseAlpha(rgba: string): number | null {
  const m = rgba.match(/rgba\([^)]+,\s*([\d.]+)\s*\)/i);
  if (!m) return null;
  const a = Number(m[1]);
  return Number.isFinite(a) ? a : null;
}

function isNearlyWhiteBg(r: number, g: number, b: number, alpha: number): boolean {
  if (alpha < 0.45) return false;
  return r > 236 && g > 236 && b > 236;
}

function isWhiteishColorValue(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (v.includes("url(") || v.includes("gradient(")) return false;
  if (v === "white") return true;

  const rgb = parseRgb(v);
  if (rgb) {
    const a = v.includes("rgba") ? parseAlpha(v) ?? 1 : 1;
    return isNearlyWhiteBg(rgb[0], rgb[1], rgb[2], a);
  }

  if (/^#[0-9a-f]{3,8}$/i.test(v)) {
    const h = v.slice(1);
    const full =
      h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6);
    if (full.length < 6) return false;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return false;
    return isNearlyWhiteBg(r, g, b, 1);
  }

  return false;
}

function rgbBrightness(r: number, g: number, b: number): number {
  return r * 0.299 + g * 0.587 + b * 0.114;
}

/** True if this text color is meant for a light background — strip so dark-theme text wins. */
function isDarkBodyText(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (v.includes("url(")) return false;
  if (v === "black") return true;

  const rgb = parseRgb(v);
  if (rgb) return rgbBrightness(rgb[0], rgb[1], rgb[2]) < 200;

  if (/^#[0-9a-f]{3,8}$/i.test(v)) {
    const h = v.slice(1);
    const full =
      h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6);
    if (full.length < 6) return false;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return false;
    return rgbBrightness(r, g, b) < 200;
  }

  return false;
}

function shouldStripColorForDarkTheme(tagName: string): boolean {
  return !/^(A|IMG|SVG|VIDEO|PICTURE|BUTTON|IFRAME)$/i.test(tagName);
}

const INLINE_BORDER_PROPS = new Set([
  "border",
  "border-top",
  "border-right",
  "border-bottom",
  "border-left",
  "border-width",
  "border-style",
  "border-color",
  "outline",
  "outline-width",
  "outline-style",
  "outline-color",
]);

const SOCIAL_HOST_RE =
  /facebook\.com|fb\.com|fb\.me|twitter\.com|t\.co|\bx\.com\b|linkedin\.com|lnkd\.in|instagram\.com|threads\.net/i;

function isSocialHref(href: string): boolean {
  return SOCIAL_HOST_RE.test(href);
}

function depthToRoot(el: Element, root: Element): number {
  let d = 0;
  let p: Element | null = el;
  while (p && p !== root) {
    d += 1;
    p = p.parentElement;
  }
  return d;
}

/** Remove Beehiiv share column (common 2-column email layout) and single-column icon rows. */
function removeSocialShareChrome(root: Element): void {
  root.querySelectorAll("tr").forEach((tr) => {
    const cells = tr.querySelectorAll(":scope > td, :scope > th");
    if (cells.length === 2) {
      const first = cells[0];
      const links = [...first.querySelectorAll("a[href]")];
      if (
        links.length >= 2 &&
        links.every((a) => isSocialHref(a.getAttribute("href") || "")) &&
        !first.querySelector("p")
      ) {
        first.remove();
        return;
      }
    }
    if (cells.length === 1) {
      const cell = cells[0];
      const links = [...cell.querySelectorAll("a[href]")];
      if (
        links.length >= 2 &&
        links.every((a) => isSocialHref(a.getAttribute("href") || "")) &&
        !cell.querySelector("p")
      ) {
        const t = (cell.textContent || "").replace(/\s+/g, " ").trim();
        if (t.length < 180) tr.remove();
      }
    }
  });

  const blocks = [...root.querySelectorAll("div, section, aside")];
  blocks.sort((a, b) => depthToRoot(b, root) - depthToRoot(a, root));
  blocks.forEach((el) => {
    const links = [...el.querySelectorAll("a[href]")];
    if (links.length < 2) return;
    if (!links.every((a) => isSocialHref(a.getAttribute("href") || ""))) return;
    if (el.querySelector("p")) return;
    const t = (el.textContent || "").replace(/\s+/g, " ").trim();
    if (t.length > 160) return;
    el.remove();
  });
}

function isHugeBottomMargin(value: string): boolean {
  const t = value.trim().toLowerCase();
  const m = t.match(/(\d+(?:\.\d+)?)\s*(px|rem)/);
  if (!m) return false;
  const n = parseFloat(m[1]);
  if (m[2] === "px") return n >= 72;
  return n >= 4.5;
}

/** Drop trailing spacer nodes Beehiiv often appends after the real article. */
function trimTrailingEmptyBlocks(root: Element): void {
  for (;;) {
    const last = root.lastElementChild;
    if (!last) break;
    if (last.tagName === "BR") {
      last.remove();
      continue;
    }
    if (last.tagName === "P" || last.tagName === "DIV") {
      const text = last.textContent?.replace(/\u00a0/g, " ").trim() ?? "";
      const raw = last.innerHTML.replace(/\u00a0/gi, " ").trim();
      if (text === "") {
        const onlyBr = /^(?:<br\s*\/?>(\s|\u200b)*)+$/i.test(raw);
        if (onlyBr || raw === "") {
          last.remove();
          continue;
        }
      }
    }
    break;
  }
}

export function sanitizeBeehiivWebHtml(html: string): string {
  if (!html || typeof document === "undefined") return html;

  const doc = new DOMParser().parseFromString(
    `<div data-beehiiv-sanitize>${html}</div>`,
    "text/html",
  );
  const root = doc.querySelector("[data-beehiiv-sanitize]");
  if (!root) return html;

  root.querySelectorAll("style, link[rel='stylesheet']").forEach((el) => {
    el.remove();
  });

  root.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const bgc = el.getAttribute("bgcolor");
    if (bgc && isWhiteishColorValue(bgc)) {
      el.removeAttribute("bgcolor");
    }

    const colorAttr = el.getAttribute("color");
    if (
      colorAttr &&
      shouldStripColorForDarkTheme(el.tagName) &&
      isDarkBodyText(colorAttr)
    ) {
      el.removeAttribute("color");
    }

    const style = el.getAttribute("style");
    if (!style) return;

    const parts = style
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);
    const kept: string[] = [];

    for (const part of parts) {
      const colon = part.indexOf(":");
      if (colon === -1) {
        kept.push(part);
        continue;
      }
      const prop = part.slice(0, colon).trim().toLowerCase();
      const val = part.slice(colon + 1).trim();

      if (prop === "background-color" && isWhiteishColorValue(val)) continue;
      if (prop === "background" && isWhiteishColorValue(val)) continue;
      if (
        prop === "color" &&
        shouldStripColorForDarkTheme(el.tagName) &&
        isDarkBodyText(val)
      ) {
        continue;
      }
      if (INLINE_BORDER_PROPS.has(prop)) {
        continue;
      }
      if (prop === "box-shadow") {
        continue;
      }
      if (
        (prop === "margin-bottom" || prop === "margin-block-end") &&
        isHugeBottomMargin(val)
      ) {
        continue;
      }
      kept.push(part);
    }

    if (kept.length === 0) el.removeAttribute("style");
    else el.setAttribute("style", kept.join("; "));
  });

  removeSocialShareChrome(root);
  trimTrailingEmptyBlocks(root);

  return root.innerHTML;
}
